import { Leaf, Resource } from "@ldo/solid";
import { useResource, useSolidAuth } from "@ldo/solid-react";
import { SolidProfileShapeShapeType } from "../.ldo/solidProfile.shapeTypes";
import { getName } from "../lib/profile";
import { parseRdf } from "@ldo/ldo";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cap } from "../lib/cap";
import { Modal } from "../components/Modal"
import { ContactPicker } from "../components/ContactPicker";
import { discoverInbox } from "../components/ImportInboxContent"
// CSS
import "./ShareCollection.css";

export const ShareCollectionModal: FunctionComponent<{
    sharedLeaf: Leaf,
    show: boolean,
    onClose: () => void
}> = ({
    sharedLeaf,
    show,
    onClose
}) => {
        const { t } = useTranslation(["spoty", "translation"]);
        const { session, fetch: authFetch } = useSolidAuth();

        const [selection, setSelection] = useState<string[]>([]);
        const [webIdsAdded, setWebIDsAdded] = useState<string[]>([]);
        const [newWebID, setNewWebID] = useState<string>();
        const [newWebIDNames, setNewWebIDNames] = useState<Record<string, string>>({});
        const [webIDStatus, setWebIDStatus] = useState<{ message: string; color: string } | null>(null);
        const [showAccessRuleModal, setShowAccessRuleModal] = useState(false);
        const [nb, setNb] = useState<number>(1);
        const [accessMode, setAccessMode] = useState<"everyone" | "selectionOnly">("selectionOnly");
        const [sendingSuccess, setSendingSuccess] = useState<{ message: string; color: string }[]>([]);
        const [accessSearchMessage, setAccessSearchMessage] = useState<string>();

        const webIdWithoutHttp = session.webId?.split('//')[1];
        const senderName = webIdWithoutHttp?.split('.')[0];
        const resource = useResource(sharedLeaf.uri);

        // Add a WebID to the list of recipients, after checking that it is a valid WebID with an inbox
        const addWebID = async () => {
            if (!newWebID) return;

            const iri = newWebID.split('#')[0];

            try {
                const resp = await fetch(iri, {
                    headers: { accept: "text/turtle" },
                });

                if (!resp.ok) {
                    console.error("HTTP error " + resp.status);
                }

                const turtle = await resp.text();
                const dataset = await parseRdf(turtle, {
                    baseIRI: iri
                });

                const profile = dataset
                    .usingType(SolidProfileShapeShapeType)
                    .fromSubject(newWebID);

                const inbox = await discoverInbox(newWebID);
                if (!inbox) {
                    setWebIDStatus({ message: t("This webID doesn't have an inbox.", { ns: "translation" }), color: "red" });
                    setTimeout(() => {
                        setWebIDStatus(null);
                    }, 3000);
                    return;
                }

                if (webIdsAdded.includes(newWebID)) {
                    setWebIDStatus({ message: t("WebID already added.", { ns: "translation" }), color: "red" });
                    setTimeout(() => {
                        setWebIDStatus(null);
                    }, 3000);
                    return;
                }
                setWebIDsAdded(old => [...old, newWebID]);
                setSelection(old => [...old, newWebID]);
                setNewWebIDNames(old => ({ ...old, [newWebID]: getName(profile) }));
                setNewWebID("");
                setWebIDStatus({ message: (t("WebID added!", { ns: "translation" })), color: "green" });
            } catch (e) {
                console.error("WebID validation error:", e);
                setWebIDStatus({ message: t("Invalid WebID", { ns: "translation" }), color: "red" });
            }
            setTimeout(() => {
                setWebIDStatus(null);
            }, 3000);
        }

        // Ensure that all recipient have access to the collection, then send it to their inboxes
        const handleShare = async () => {
            setAccessSearchMessage(t("Searching for permissions...", { ns: "translation" }));
            let wacRes = await resource.getWac({ ignoreCache: true });

            if (wacRes.type !== "getWacRuleSuccess") {
                console.warn("Could not read resource permissions.");
                setShowAccessRuleModal(true);
            } else {
                const perm = JSON.parse(JSON.stringify(wacRes.wacRule));
                let hasMissingAccess = false;

                for (const id of selection) {
                    const hasReadAccess = perm.public?.read || perm.authenticated?.read || perm.agent?.[id]?.read;
                    if (!hasReadAccess) {
                        hasMissingAccess = true;
                        break;
                    }
                }

                if (hasMissingAccess) {
                    setShowAccessRuleModal(true);
                } else {
                    setShowAccessRuleModal(false);
                    if (!senderName) return;
                    sendLeafToInbox(sharedLeaf, selection, resource, senderName, nb, t, setSendingSuccess, setNb, accessMode, authFetch);
                }
            }
        }

        useEffect(() => {
            if (!show) {
                setShowAccessRuleModal(false);
                setWebIDsAdded([]);
                setSendingSuccess([]);
                setWebIDStatus(null);
                setAccessSearchMessage("");
            }
        }, [show]);

        return <Modal show={show} setShow={onClose}>
            {showAccessRuleModal ?
                <AccessRulesModal sharedLeaf={sharedLeaf} selection={selection} accessMode={accessMode} setAccessMode={setAccessMode} sendingSuccess={sendingSuccess} setSendingSuccess={setSendingSuccess} nb={nb} setNb={setNb} senderName={senderName ?? "anonymous"} />
                : sendingSuccess.length > 0 ?
                    <ul>
                        {sendingSuccess.map((msg, index) => (
                            <li key={index} style={{ color: msg.color, marginBottom: "0.5em" }}>
                                {msg.message}
                            </li>
                        ))}
                    </ul>
                    : <>
                        <ContactPicker select={(val) => setSelection(Array.isArray(val) ? val : [val])}
                            multiple={true}></ContactPicker>
                        <ul id="webIDsAdded">
                            {webIdsAdded.map((id) => (
                                <li key={id}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={selection.includes(id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelection([...selection, id]);
                                                } else {
                                                    setSelection(selection.filter((selectedId) => selectedId !== id));
                                                }
                                            }}
                                        />
                                        {newWebIDNames[id]}
                                    </label>
                                </li>
                            ))}
                        </ul>
                        <div id="addNewWebID">
                        <label htmlFor="webIDInput">{t("Enter a webID:", { ns: "translation" })}</label>
                            <input id="webIDInput" type="text" value={newWebID} onChange={(e) => {
                                setNewWebID(e.target.value);
                            }}></input>
                        <button id="addButton" onClick={addWebID}>{cap(t("add", { ns: "translation" }))}</button>
                        </div>
                        <p style={{ color: webIDStatus?.color }}>{webIDStatus?.message}</p>
                        <button id="sendButton" onClick={handleShare}>{cap(t("validate", { ns: "translation" }))}</button>
                        <p>{accessSearchMessage}</p>
                    </>
            }
        </Modal>
    }

const AccessRulesModal: FunctionComponent<{
    sharedLeaf: Leaf,
    selection: string[],
    accessMode: "everyone" | "selectionOnly",
    setAccessMode: React.Dispatch<React.SetStateAction<"everyone" | "selectionOnly">>,
    sendingSuccess: { message: string; color: string }[],
    setSendingSuccess: React.Dispatch<React.SetStateAction<{ message: string; color: string }[]>>,
    nb: number,
    setNb: React.Dispatch<React.SetStateAction<number>>,
    senderName: string,
}> = ({
    sharedLeaf,
    selection,
    accessMode,
    setAccessMode,
    sendingSuccess,
    setSendingSuccess,
    nb,
    setNb,
    senderName
}) => {
        const { t } = useTranslation(["spoty", "translation"]);
        const { fetch: authFetch } = useSolidAuth();

        const [accessMessage, setAccessMessage] = useState<string>();

        const resource = useResource(sharedLeaf.uri);

        return <>
            {sendingSuccess.length === 0 && (<>
                <h2>{t("Some of your contacts do not have permission to read your file. To whom would you like to give read access to your file?", { ns: "translation" })}</h2>
                <label>
                    <input type="radio" checked={accessMode === "everyone"} name="accessRulesInput" value="everyone" onChange={() => setAccessMode("everyone")} />
                    {t("to everyone", { ns: "translation" })}
                </label>
                <label>
                    <input type="radio" checked={accessMode === "selectionOnly"} name="accessRulesInput" value="selectionOnly" onChange={() => setAccessMode("selectionOnly")} />
                    {t("only to my selection", { ns: "translation" })}
                </label>
                <button id="sendButton" onClick={() => {
                    setAccessMessage(t("Granting access to your file...", { ns: "translation" }));
                    sendLeafToInbox(sharedLeaf, selection, resource, senderName, nb, t, setSendingSuccess, setNb, accessMode, authFetch);
                }}>{cap(t("send", { ns: "translation" }))}</button>
                <p>{accessMessage}</p>
            </>)}

            <ul>
                {sendingSuccess.map((msg, index) => (
                    <li key={index} style={{ color: msg.color, marginBottom: "0.5em" }}>
                        {msg.message}
                    </li>
                ))}
            </ul>
        </>
    }

const sendLeafToInbox = async (
    sharedLeaf: Leaf,
    selection: string[],
    resource: Resource,
    senderName: string,
    nb: number,
    t: Function,
    setSendingSuccess: React.Dispatch<React.SetStateAction<{ message: string; color: string }[]>>,
    setNb: React.Dispatch<React.SetStateAction<number>>,
    accessMode: "everyone" | "selectionOnly",
    authFetch: typeof fetch
) => {
    if (!sharedLeaf) return;

    for (const id of selection) {
        const inbox = await discoverInbox(id);
        if (!inbox) {
            setSendingSuccess([{ message: t("Error: Inbox URL not found.", { ns: "translation" }), color: "red" }]);
            setTimeout(() => setSendingSuccess([]), 3000);
            return;
        }

        const contentToSend = `
      <>
        <http://www.w3.org/1999/02/22-rdf-syntax-ns#type> <https://w3id.org/SpOTy/ontology#RemoteSentenceCollection> ;
        <http://www.w3.org/2000/01/rdf-schema#seeAlso> <${sharedLeaf.uri}> .
    `;
        const sharedLeafName = sharedLeaf.uri.split("/")[4];
        const slug = t("RemoteSentenceCollection {{sharedLeafName}} {{nb}} sent by {{senderName}}", {
            sharedLeafName, nb, senderName
        });

        await checkAccessRules(resource, id, accessMode);

        try {
            await sendToInbox(inbox, contentToSend, slug, authFetch);
            setSendingSuccess((prev) => [...prev, { message: t("Your sentence collection has been shared with {{id}}!", { id }), color: "green" }]);
            setNb(nb + 1);
        } catch (e) {
            setSendingSuccess([{ message: t("An error occurred while sending your sentence collection."), color: "red" }]);
        }
    }
};

// To check access rules before sending
const checkAccessRules = async (resource: Resource, webID: string, accessMode: "everyone" | "selectionOnly") => {
    let wacRes = await resource.getWac({ ignoreCache: true });

    if (wacRes.type !== "getWacRuleSuccess") {
        console.warn("Could not read resource permissions.");
        return;
    }

    const perm = JSON.parse(JSON.stringify(wacRes.wacRule));

    if (accessMode === "everyone") {
        perm.public.read = true;
    } else {
        if (!perm.agent[webID]) {
            perm.agent[webID] = {
                read: true,
                write: false,
                append: false,
                control: false,
            }
        } else {
            perm.agent[webID].read = true;
        }
    }

    let wacRes2 = await resource.setWac(perm);
    if (wacRes2.type === "setWacRuleSuccess") {
        console.debug("New permissions set for " + resource.uri);
    } else {
        alert("Error while setting permissions");
        return;
    }
}

// To send to inbox
const sendToInbox = async (inboxUrl: string, contentToSend: string, slug: string, authFetch: typeof fetch) => {
    const resp = await authFetch(inboxUrl, {
        method: "POST",
        headers: {
            "Content-Type": "text/turtle",
            Slug: slug
        },
        credentials: "include",
        body: contentToSend
    });

    if (!resp.ok) {
        console.error(`Failed to post to inbox: ${resp.statusText}`);
    }
    return resp;
}
